import { useLocation, useParams } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../helper/hooks/useAuth';
import { useEffect, useState } from 'react';
import { useStoreState } from 'easy-peasy';
import { exportVideoAPI } from '../dashboard.service';
import { useProjects } from '../../helper/hooks/useProjects';
import { useProjectStatus } from '../../helper/hooks/useProjectStatus';
import Navleft from './nav-left/NavLeft';
import NavRight from './nav-right/NavRight';
import toast from 'react-hot-toast';
import { useExport } from '../../helper/hooks/useExport';
import { subscribeToSubscription, subscribeToUser } from '../plan/plans.service';
import TagManager from 'react-gtm-module';
import UpgradeModal from '../../shared/upgrade-modal/UpgradeModal';

const Navbar = ({ onToggleSidebar, sidebarOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const location = useLocation();
  const isProjectRoute = location.pathname.startsWith('/projects');
  const { user, setCredits, setSubscription, setBillingCountry, subscription, setIsFreePlan } =
    useAuth();
  const { projectId } = useParams();
  const { currentProject, assetsLoaded, version } = useProjects();
  const [hasVideo, setHasVideo] = useState(!!currentProject?.video);
  const { exportStatus, progress, videoUrl, setUpdateState, isUpdating, setIsUpdating } = useExport(
    {
      projectId: projectId || null
    }
  );
  const { status: projectStatus, isTimelineReady } = useProjectStatus(projectId);
  const isTranslating = projectStatus === 'PPTX_TRANSLATING';
  const isExtractionDone = projectStatus === 'EXTRACTION_DONE';
  const isUpdatingScripts = useStoreState(
    (state) => !!state.ppt.updatingScriptsByProject[projectId]
  );
  const canExport = isExtractionDone && isTimelineReady && !isTranslating && !isUpdatingScripts;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    setHasVideo(!!videoUrl);
  }, [videoUrl]);

  // Real-time listener for user data (credits and subscription)
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = subscribeToUser(
      user.uid,
      (userData) => {
        if (userData) {
          if (userData.credits !== undefined && userData.credits !== null) {
            setCredits(userData.credits);
          } else {
            setCredits(null);
          }
          const subscriptionId = userData.subscriptionId ?? null;
          setSubscription(subscriptionId);
          if (!subscriptionId) {
            setIsFreePlan(null);
          }
          if (userData.billingCountry) {
            setBillingCountry(userData.billingCountry);
          }
        } else {
          setCredits(null);
          setSubscription(null);
          setBillingCountry(null);
          setIsFreePlan(null);
        }
      },
      (error) => {
        console.error('Error listening to user data:', error);
        toast.error('Failed to load credits. Please refresh the page.');
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.uid, setCredits, setSubscription, setBillingCountry]);

  // Real-time listener for subscription changes to update free plan status
  useEffect(() => {
    if (!subscription) {
      return;
    }

    const unsubscribe = subscribeToSubscription(
      subscription,
      (data) => {
        const isFreePlan = Boolean(data && data.isPaid !== true && !data.corporateCredits);
        setIsFreePlan(isFreePlan);
      },
      (error) => {
        console.error('Subscription listener error:', error);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [subscription, setIsFreePlan]);

  const handleGenerate = async () => {
    if (!projectId) {
      toast.error('No project ID found');
      return;
    }

    if (!canExport) {
      toast.error('Please wait until the translated timeline is ready before exporting.');
      return;
    }

    try {
      // Track video export initiation with GTM
      TagManager.dataLayer({
        event: 'video_export_click',
        userId: user?.uid,
        projectId: projectId
      });

      toast.loading('Initiating video export...', { id: 'generate' });
      const response = await exportVideoAPI(projectId);

      if (response.status === 'SUCCESS') {
        // Track video export success with GTM
        TagManager.dataLayer({
          event: 'video_export_success',
          userId: user?.uid,
          projectId: projectId
        });

        toast.success('Video export started!', { id: 'generate' });
      } else if (response.status === 'ERROR') {
        switch (response.error) {
          case 'UNAUTHORIZED':
            toast.error('You are not authorized to export this video', { id: 'generate' });
            break;
          case 'BAD_REQUEST':
            toast.error(response?.message, { id: 'generate' });
            break;
          case 'NO_CREDITS':
            toast.error('You do not have enough credits to export this video', { id: 'generate' });
            setShowUpgradeModal(true);
            break;
          default:
            toast.error(`Failed to export video: ${response.error}`, { id: 'generate' });
        }
      } else {
        throw new Error('Unexpected response');
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error('Failed to export video', { id: 'generate' });
    }
  };

  const handleShare = () => {
    if (!projectId) {
      toast.error('No project ID found');
      return;
    }

    const websiteUrl = process.env.REACT_APP_WEBSITE_URL;
    const shareUrl = `${websiteUrl}/share/${projectId}`;
    window.open(shareUrl, '_blank');
  };

  const handleUpdate = async () => {
    if (!projectId) {
      toast.error('No project ID found');
      return;
    }

    if (!canExport) {
      toast.error('Please wait until the translated timeline is ready before updating.');
      return;
    }

    try {
      toast.loading('Updating video export...', { id: 'update' });
      setUpdateState();

      const response = await exportVideoAPI(projectId);

      if (response.status === 'SUCCESS') {
        toast.success('Video update started!', { id: 'update' });
      } else if (response.status === 'ERROR') {
        switch (response.error) {
          case 'UNAUTHORIZED':
            toast.error('You are not authorized to update this video', { id: 'update' });
            break;
          case 'BAD_REQUEST':
            toast.error(response?.message, { id: 'update' });
            break;
          case 'NO_CREDITS':
            toast.error('You do not have enough credits to update this video', { id: 'update' });
            setShowUpgradeModal(true);
            break;
          default:
            toast.error(`Failed to update video: ${response.error}`, { id: 'update' });
        }
        localStorage.removeItem(`updating_${projectId}`);
        setIsUpdating(false);
      } else {
        throw new Error('Unexpected response');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error(`Failed to update video: ${error.message}`, { id: 'update' });
      localStorage.removeItem(`updating_${projectId}`);
      setIsUpdating(false);
    }
  };

  return (
    <>
      <div className="navbar-container">
        <Navleft
          onToggleSidebar={onToggleSidebar}
          sidebarOpen={sidebarOpen}
          isMobile={isMobile}
          projectId={projectId}
          isProjectRoute={isProjectRoute}
        />
        <NavRight
          isMobile={isMobile}
          isTablet={isTablet}
          isProjectRoute={isProjectRoute}
          projectId={projectId}
          hasVideo={hasVideo}
          videoUrl={videoUrl}
          updating={isUpdating}
          assetsLoaded={assetsLoaded}
          version={version}
          exportStatus={exportStatus}
          progress={progress}
          isTranslating={isTranslating}
          canExport={canExport}
          projectName={currentProject?.metadata?.name || ''}
          handleGenerate={handleGenerate}
          handleUpdate={handleUpdate}
          handleShare={handleShare}
        />

        <UpgradeModal open={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      </div>

      <style jsx>{`
        .navbar-container {
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: transparent;
          border: none;
          position: fixed;
          top: 0;
          right: 0;
          left: 0;
          z-index: 1100;
        }
      `}</style>
    </>
  );
};

export default Navbar;
